import { useEffect, useMemo, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Undo2, ShieldAlert, RefreshCw, Archive } from "lucide-react";
import { toast } from "sonner";
import { useSelection } from "@/lib/useSelection";

/**
 * /dashboard/admin/trash — soft-deleted records across all managed collections.
 *
 * System Administrators can:
 *   • Filter by collection.
 *   • Restore individual records (via existing lifecycle `/restore` endpoint).
 *   • Bulk-restore selected records.
 *   • Bulk-purge selected records (physical delete + audit snapshot).
 *   • Empty the entire trash (super-purge, double-confirmation required).
 */
export default function AdminTrash() {
  const [items, setItems] = useState([]);
  const [byCollection, setByCollection] = useState({});
  const [filterColl, setFilterColl] = useState("all");
  const [loading, setLoading] = useState(true);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [purgeText, setPurgeText] = useState("");
  const [emptyReason, setEmptyReason] = useState("");
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filterColl !== "all" ? { collection: filterColl } : {};
      const { data } = await api.get("/admin/records/trash", { params });
      setItems(data.items || []);
      setByCollection(data.by_collection || {});
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterColl]);

  const selectableRows = useMemo(() => items.map((it) => ({ id: `${it.collection}::${it.id}` })), [items]);
  const selection = useSelection(selectableRows);

  const selectedByCollection = useMemo(() => {
    const groups = {};
    Array.from(selection.selected).forEach((key) => {
      const [coll, id] = key.split("::");
      (groups[coll] = groups[coll] || []).push(id);
    });
    return groups;
  }, [selection.selected]);

  const restoreOne = async (coll, id) => {
    try {
      await api.post(`/admin/records/${coll}/${id}/restore`);
      toast.success("Record restored");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const purgeOne = async (coll, id) => {
    if (!window.confirm("Permanently delete this record? This cannot be undone.")) return;
    try {
      await api.post(`/admin/records/${coll}/${id}/purge`, null, { headers: { "X-Confirm-Purge": "yes" } });
      toast.success("Record purged");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const bulkRestore = async () => {
    setWorking(true);
    try {
      let restored = 0;
      for (const [coll, ids] of Object.entries(selectedByCollection)) {
        // Use existing lifecycle endpoint per record. Fast for < 500 rows.
        for (const id of ids) {
          await api.post(`/admin/records/${coll}/${id}/restore`);
          restored += 1;
        }
      }
      toast.success(`${restored} record(s) restored`);
      selection.clear();
      load();
    } catch (e) { toast.error(errMessage(e)); }
    finally { setWorking(false); }
  };

  const bulkPurge = async () => {
    if (purgeText.trim().toUpperCase() !== "PURGE") {
      toast.error("Type PURGE to confirm");
      return;
    }
    setWorking(true);
    try {
      let purged = 0;
      for (const [coll, ids] of Object.entries(selectedByCollection)) {
        const { data } = await api.post(
          `/admin/records/${coll}/bulk-purge`,
          { ids, reason: emptyReason, confirmation: "PURGE" },
          { headers: { "X-Confirm-Purge": "yes" } },
        );
        purged += data.purged || 0;
      }
      toast.success(`${purged} record(s) permanently purged`);
      selection.clear();
      setPurgeText("");
      setEmptyReason("");
      load();
    } catch (e) { toast.error(errMessage(e)); }
    finally { setWorking(false); }
  };

  const emptyTrash = async () => {
    if (purgeText.trim().toUpperCase() !== "PURGE") {
      toast.error("Type PURGE to confirm");
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post(
        "/admin/records/trash/empty",
        { ids: [], reason: emptyReason, confirmation: "PURGE" },
        { headers: { "X-Confirm-Purge": "yes" } },
      );
      toast.success(`Trash emptied — ${data.purged} record(s) removed`);
      setEmptyOpen(false);
      setPurgeText("");
      setEmptyReason("");
      selection.clear();
      load();
    } catch (e) { toast.error(errMessage(e)); }
    finally { setWorking(false); }
  };

  const totalDeleted = items.length;
  const collectionOptions = Object.keys(byCollection).length
    ? Object.keys(byCollection)
    : ["credentials", "licenses", "schools", "school_applications", "applications", "renewals", "ce_records", "disciplinary_records", "reinstatement_records", "site_visits", "payments", "email_deliveries", "audit_logs"];

  return (
    <div className="animate-fade-up" data-testid="admin-trash-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Trash2 className="h-7 w-7" /> Deleted records
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Records here have been soft-deleted (hidden from all lists + the public registry) but are
            physically retained. System Administrators can restore, permanently purge, or empty the
            entire trash. Every action is written to the audit log.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="trash-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="destructive" onClick={() => setEmptyOpen(true)} data-testid="trash-empty-btn" disabled={totalDeleted === 0}>
            <ShieldAlert className="h-4 w-4 mr-2" /> Empty trash
          </Button>
        </div>
      </div>

      <Card className="p-4 border-slate-200 mb-5 flex flex-wrap items-center gap-3">
        <Label className="text-xs font-medium">Filter by collection</Label>
        <Select value={filterColl} onValueChange={setFilterColl}>
          <SelectTrigger className="w-64" data-testid="trash-collection-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All collections ({totalDeleted})</SelectItem>
            {collectionOptions.map((c) => (
              <SelectItem key={c} value={c}>{c} ({byCollection[c] ?? 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <span className="text-xs text-slate-500" data-testid="trash-total-count">
          {totalDeleted} deleted record{totalDeleted === 1 ? "" : "s"}
        </span>
      </Card>

      {selection.count > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-md" data-testid="trash-bulk-bar">
          <span className="text-sm font-medium text-amber-900 mr-2" data-testid="trash-selected-count">
            {selection.count} record{selection.count === 1 ? "" : "s"} selected
          </span>
          <Button variant="ghost" size="sm" onClick={selection.clear} className="text-amber-800 hover:bg-amber-100" data-testid="trash-clear-selection">
            Deselect
          </Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={bulkRestore} disabled={working} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="trash-bulk-restore">
            <Undo2 className="h-4 w-4 mr-1.5" /> Restore selected
          </Button>
          <BulkPurgeButton
            purgeText={purgeText}
            setPurgeText={setPurgeText}
            emptyReason={emptyReason}
            setEmptyReason={setEmptyReason}
            onConfirm={bulkPurge}
            working={working}
            count={selection.count}
          />
        </div>
      )}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                <th className="p-3 w-10">
                  <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="trash-select-all" />
                </th>
                <th className="p-3">Collection</th>
                <th className="p-3">Label</th>
                <th className="p-3">Deleted at</th>
                <th className="p-3">Reason</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center">
                  <Archive className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <div className="font-heading font-semibold text-primary">Trash is empty</div>
                  <div className="text-sm text-slate-500 mt-1">No soft-deleted records to display.</div>
                </td></tr>
              )}
              {items.map((it) => {
                const key = `${it.collection}::${it.id}`;
                return (
                  <tr key={key} className="hover:bg-slate-50" data-testid={`trash-row-${it.collection}-${it.id}`}>
                    <td className="p-3">
                      <Checkbox checked={selection.isSelected(key)} onCheckedChange={() => selection.toggle(key)} data-testid={`trash-select-${it.collection}-${it.id}`} />
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono-data text-[10px]">{it.collection}</Badge>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-primary text-sm truncate max-w-xs" title={it.label}>{it.label}</div>
                      <div className="text-[10px] font-mono-data text-slate-500 truncate max-w-xs" title={it.id}>{it.id}</div>
                    </td>
                    <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                      {it.deleted_at ? new Date(it.deleted_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 text-xs text-slate-600 max-w-xs truncate" title={it.delete_reason}>
                      {it.delete_reason || "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => restoreOne(it.collection, it.id)} className="text-emerald-700 hover:bg-emerald-50" data-testid={`trash-restore-${it.collection}-${it.id}`}>
                          <Undo2 className="h-3.5 w-3.5 mr-1" /> Restore
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => purgeOne(it.collection, it.id)} className="text-red-700 hover:bg-red-50" data-testid={`trash-purge-${it.collection}-${it.id}`}>
                          <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Purge
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={emptyOpen} onOpenChange={(o) => !o && setEmptyOpen(false)}>
        <AlertDialogContent data-testid="trash-empty-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Empty the entire trash?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>all {totalDeleted}</b> soft-deleted record(s) across every collection.
              A snapshot of each is written to the audit log before deletion. This cannot be undone.
              Type <b>PURGE</b> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea rows={2} value={emptyReason} onChange={(e) => setEmptyReason(e.target.value)} data-testid="trash-empty-reason" />
            </div>
            <div>
              <Label className="text-xs">Type PURGE to confirm</Label>
              <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="PURGE" data-testid="trash-empty-confirm-text" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-empty-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={working || purgeText.trim().toUpperCase() !== "PURGE"}
              onClick={emptyTrash}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="trash-empty-confirm"
            >
              Empty trash ({totalDeleted})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BulkPurgeButton({ purgeText, setPurgeText, emptyReason, setEmptyReason, onConfirm, working, count }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-red-500 text-red-700 hover:bg-red-100"
        data-testid="trash-bulk-purge"
      >
        <ShieldAlert className="h-4 w-4 mr-1.5" /> Purge permanently
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <AlertDialogContent data-testid="trash-bulk-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Purge {count} selected record{count === 1 ? "" : "s"} permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. A snapshot is written to the audit log before deletion.
              Type <b>PURGE</b> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea rows={2} value={emptyReason} onChange={(e) => setEmptyReason(e.target.value)} data-testid="trash-bulk-purge-reason" />
            </div>
            <div>
              <Label className="text-xs">Type PURGE to confirm</Label>
              <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="PURGE" data-testid="trash-bulk-purge-text" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-bulk-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={working || purgeText.trim().toUpperCase() !== "PURGE"}
              onClick={async () => { await onConfirm(); setOpen(false); }}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="trash-bulk-purge-confirm"
            >
              Purge {count}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
